import axios, { CancelToken, CancelTokenSource } from 'axios';
import Multiselect from 'vue-multiselect';
import IClientApp from "@/interfaces/IClientApp";
import transformDateFields from "@/libs/date/transformDateFields";
import isEmpty from "@/libs/isEmpty";
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter, Mutation } from 'vuex-class';

import Loader from '@/components/Includes/Loader/Loader.vue';
import Pagination from '@/components/Includes/Pagination/Pagination.vue';
import SelectionColumnsInTable from '@/components/Includes/SelectionColumnsInTable/SelectionColumnsInTable.vue';
import TableEmpty from '@/components/Includes/Table/TableFooter/TableEmpty/TableEmpty.vue';
import ICountry from '@/interfaces/countries/ICountry';
import ICampaign from '@/interfaces/ICampaign';
import ILanding from '@/interfaces/ILanding';
import IPayloadQuery from '@/interfaces/IPayloadQuery';
import ITableCol from '@/interfaces/ITableCol';
import ITrafficSource from '@/interfaces/ITrafficSource';
import IMultiSelectItem from '@/interfaces/selects/IMultiSelectItem';
import ISelectItemDefault from '@/interfaces/selects/ISelectItemDefault';
import dateToStandart from '@/libs/date/dateToStandart';
import getTableColumns from '@/libs/getTableColumns';
import searchByTable from '@/libs/searchQuery/searchByTable';

import { DISPLAY_MODE_FACEBOOK } from '@/../configs/displayModes';
import urlToId from '@/libs/urlToId';
import CampaignsListTools from './CampaignsListTools/CampaignsListTools.vue';
import IDeviceType from '@/interfaces/IDeviceType';
import { stringify } from 'qs';

@Component({
    name: 'CampaignsList',
    components: {
        CampaignsListTools,
        TableEmpty,
        Pagination,
        SelectionColumnsInTable,
        Loader,
        Multiselect
    }
})
export default class CampaignsList extends Vue {
    @Action private deviceTypesList!: () => Promise<void>;
    @Action private getCampaign!: (params: {id: number, cancelToken?: CancelToken}) => Promise<ICampaign>;
    @Action private campaignsList!: (query: IPayloadQuery) => Promise<{data: ICampaign[], links: number}>;
    @Action private landingsList!: (query: IPayloadQuery) => Promise<void>;
    @Action private countriesList!: (query: IPayloadQuery) => Promise<void>;
    @Action private fetchTrafficSourcesById!: (ids: string[]) => Promise<void>;

    @Mutation private uploadTrafficSources!: (
      trafficSources: ITrafficSource[]
    ) => void;

    @Mutation private uploadLandings!: (landings:ILanding[]) => void;

    @Getter private getTrafficSourceById!: (
      id: string
    ) => ITrafficSource | undefined;
    private getCampaigns: ICampaign[] = [];
    @Getter private getLandings!: () => ILanding[];
    @Getter private getTrafficSources!: () => ITrafficSource[];
    @Getter private getCountries!: () => ICountry[];

    @Getter private getDeviceTypes!: () => IDeviceType[];

    // Begin Vuex Logic for Client Apps
    @Action private clientAppsList!: (query: IPayloadQuery) => Promise<void>;
    @Getter private getClientApps!: () => IClientApp[];
    // End Vuex Logic for Client Apps

    private selectedTableColumns: IMultiSelectItem[] = [];

    private orderName: string = 'id';
    private orderType: boolean = true; // true/false === 'desc'/'asc'
    private selectedDeviceType: IMultiSelectItem[] = [];
    private selectedLimit: string = '100';
    private selectedDisplayMode = '';
    private limits: ISelectItemDefault[] = [
        {value: '100', text: '100'},
        {value: '250', text: '250'},
        {value: '500', text: '500'}
    ]
    private totalCampaigns: number = 0;
    private id = '';
    private title = '';
    private campaignId = '';
    private loading: boolean = true;
    private filtered: ICampaign[] | null = null;

    private get deviceTypes(): IMultiSelectItem[] {
        return this.getDeviceTypes().map(deviceType => ({
            label: deviceType.name,
            value: deviceType.id,
        }));
    }

    private get currentPage(): number {
        return +this.$route.query.page || 1;
    }

    private set currentPage(page: number) {
        const query = {...this.$route.query, page: `${page}`};

        this.$router.push({ query });
    }
    
    private get totalItems():number {
        return this.filtered ? this.filtered.length : this.totalCampaigns;
    }

    private get tableColumns(): ITableCol[] | undefined {
        const exclude: string[] = [];

        if (this.selectedDisplayMode === DISPLAY_MODE_FACEBOOK) {
            exclude.push(
                'installPostbackUrl',
                'installPostbackUrlEnabled',
                'trialPostbackUrl',
                'trialPostbackUrlEnabled',
                'subscriptionPostbackUrl',
                'subscriptionPostbackUrlEnabled',
            );
        }

        return getTableColumns(this.campaigns, true, exclude);
    };

    private get fields() {
        if (this.tableColumns) {
            const columnsIds = this.selectedTableColumns.map((el: IMultiSelectItem) => el.value);
            return this.tableColumns.filter((el: any) => columnsIds.includes(el.key));
        } else {
            return this.tableColumns;
        }
    }

    private get campaignsData(): ICampaign[] {
        return this.filtered || this.campaigns;
    }

    private get payloadQuery(): IPayloadQuery {
        return {
            query: `?${stringify({
                pagination: true,
                page: this.currentPage,
                limit: this.selectedLimit,
                deviceTypeId: this.selectedDeviceType.map(({value}) => value),
                campaignId: this.campaignId,
                title: this.title,
                id: this.id
            }, {arrayFormat: 'brackets'})}`
        };
    }

    private get emptyDataText(): string | undefined {
        if (Array.isArray(this.filtered) && this.filtered.length === 0) {
            return 'Campaign not found'
        }
    }

    private notEmpty(data: any) {
        return data.length !== 0;
    }

    private nameTrafficSource(source: string): string {
      const id = urlToId(source);
      const item = this.getTrafficSourceById(id);
      return item ? item.title : "";
    }

    private nameLanding(id: number): string {
        console.log(id);
        const item: ILanding | undefined = this.landings.find((landing: ILanding) => landing.id === id);
        return item ? item.title : ''
    }

    private nameCountry(id: number): string {
        const item: ICountry | undefined = this.countries.find((country: ICountry) => country.id === id);
        return item ? item.name : ''
    }

    private campaignDetail(campaign: any) {
        this.$router.push({
            name: 'campaign_detail',
            params: {id: campaign.id, campaignData: campaign}
        });
    }

    private updateData(key: 'title' | 'campaignId' | 'id', value: string) {
        this.currentPage = 1;
        this[key] = value;
    }


    private get campaigns() {
        return this.getCampaigns.map((campaign: ICampaign) => {
            const newCampaign = {...campaign};

            if (campaign.createdAt) {
                newCampaign.createdAt = dateToStandart(campaign.createdAt);
            }

            if (campaign.updatedAt) {
                newCampaign.updatedAt = dateToStandart(campaign.updatedAt);
            }

            return newCampaign;
        });
    }

    private get clientApps(): IClientApp[] {
        return this.getClientApps();
    }

    private get trafficSources(): ITrafficSource[] {
        return this.getTrafficSources();
    }

    private get landings(): ILanding[] {
        return this.getLandings();
    }

    private get countries(): ICountry[] {
        return this.getCountries();
    }

    private onFilter() {
        this.loadCampaigns({payloadQuery: this.payloadQuery});
    }

    @Watch("campaigns", { immediate: true })
    private onChangeCampaigns(campaigns: ICampaign[]) {
      const ids = campaigns.reduce(
        (acc: { [key: string]: string }, { trafficSource }) => {
          const id = urlToId(trafficSource);

          if (id) {
            acc[id] = id;
          }

          return acc;
        },
        {}
      );

      this.fetchTrafficSourcesById(Object.values(ids));
    }

    private async loadCampaigns(options: { enableLoading?: boolean, payloadQuery: IPayloadQuery }) {
        const { enableLoading = true, payloadQuery} = options;

        if (enableLoading) {
            this.loading = true;
        }
        
        const {links, data} = await this.campaignsList(payloadQuery);

        this.totalCampaigns = links;
        this.getCampaigns = data;
 
        this.checkDateFields();

        if (enableLoading) {
            this.loading = false;
        }
    }

    // Begin Logic for Relations

    private checkClientApp(clientAppId:number) {
        return this.clientApps.find((clientApp: IClientApp) => clientApp.id === Number(clientAppId));
    }

    private getDeviceTypeName(id: number) {
        const deviceType = this.deviceTypes.find(({value}) => value === id);

        return deviceType ? deviceType.label : '';
    }

    // Begin Transform Data for Form Campaign
    private checkDateFields() {
        const dateFields: string[] = ['createdAt', 'updatedAt'];
        transformDateFields(dateFields, this.campaigns)
    }

    // End Transform Data for Form Campaign

    private async mounted() {
        this.uploadTrafficSources([]);
        this.uploadLandings([]);
        this.deviceTypesList();
        this.loading = true;
        const dependecies = [];
        try {

            if (isEmpty(this.clientApps)) {
                dependecies.push(this.clientAppsList({ query: '?pagination=false' }));
            }
            if (isEmpty(this.landings)) {
                dependecies.push(this.landingsList({ query: '?pagination=false' }));
            }
            if (isEmpty(this.countries)) {
                dependecies.push(this.countriesList({ query: '?pagination=false' }));
            }

            await Promise.all(dependecies);
            await this.loadCampaigns({enableLoading: false, payloadQuery: this.payloadQuery});

        } catch {
            return void 0;
        }

        this.loading = false;
    }
}
